package cz.lhoracek.mapl.ui.screens.addround

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import cz.lhoracek.mapl.data.model.GameType
import cz.lhoracek.mapl.data.model.Player
import cz.lhoracek.mapl.data.model.Round
import cz.lhoracek.mapl.data.model.SessionWithPlayers
import cz.lhoracek.mapl.data.model.Suit
import cz.lhoracek.mapl.data.repository.SessionRepository
import cz.lhoracek.mapl.domain.ScoreCalculator
import cz.lhoracek.mapl.domain.SessionScoreCalculator
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class AddRoundUiState(
    val actorId: String = "",
    val suit: Suit = Suit.ZALUDY,
    val gameType: GameType = GameType.TRI,
    val flekLevel: Int = 0,
    val won: Boolean = true,
    val isSaving: Boolean = false,
    val savedOk: Boolean = false,
)

class AddRoundViewModel(
    private val repository: SessionRepository,
    private val sessionId: String,
) : ViewModel() {

    val session: StateFlow<SessionWithPlayers?> =
        repository.observeSessionWithPlayers(sessionId)
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), null)

    private val _uiState = MutableStateFlow(AddRoundUiState())
    val uiState: StateFlow<AddRoundUiState> = _uiState.asStateFlow()

    fun setActor(playerId: String) = _uiState.update { it.copy(actorId = playerId) }
    fun setSuit(suit: Suit) = _uiState.update { it.copy(suit = suit) }
    fun setGameType(gameType: GameType) = _uiState.update { it.copy(gameType = gameType) }
    fun setFlekLevel(level: Int) = _uiState.update { it.copy(flekLevel = level.coerceIn(0, 6)) }
    fun setWon(won: Boolean) = _uiState.update { it.copy(won = won) }

    fun previewDeltas(players: List<Player>, currentBet: Int, won: Boolean = _uiState.value.won): Map<String, Int> {
        val state = _uiState.value
        if (state.actorId.isBlank()) return emptyMap()
        val dummyRound = Round(
            id = "preview",
            sessionId = sessionId,
            actorId = state.actorId,
            suit = state.suit,
            gameType = state.gameType,
            flekLevel = state.flekLevel,
            won = won,
        )
        val deltas = ScoreCalculator.calculateDeltas(dummyRound, players, currentBet).toMutableMap()
        val sessionData = session.value
        val isPotPayoutRound = won &&
            (state.gameType == GameType.CTYRI || state.gameType == GameType.MAPL)
        if (sessionData?.session?.potEnabled == true && isPotPayoutRound) {
            val currentPot = SessionScoreCalculator.calculate(
                session = sessionData.session,
                players = sessionData.players,
                rounds = sessionData.rounds,
            ).currentPot
            deltas[state.actorId] = (deltas[state.actorId] ?: 0) + currentPot
        }
        return deltas
    }

    fun confirm() {
        val state = _uiState.value
        if (state.actorId.isBlank() || state.isSaving) return
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            val round = Round(
                id = "r_${cz.lhoracek.mapl.data.repository.currentTimeMillis()}",
                sessionId = sessionId,
                actorId = state.actorId,
                suit = state.suit,
                gameType = state.gameType,
                flekLevel = state.flekLevel,
                won = state.won,
                timestamp = cz.lhoracek.mapl.data.repository.currentTimeMillis(),
                bet = session.value?.session?.currentBet ?: 0,
            )
            repository.addRound(sessionId, round)
            _uiState.update { it.copy(isSaving = false, savedOk = true) }
        }
    }

    fun confirmNoCaller() {
        val sessionData = session.value ?: return
        if (!sessionData.session.potEnabled || _uiState.value.isSaving) return

        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            val round = Round(
                id = "r_${cz.lhoracek.mapl.data.repository.currentTimeMillis()}",
                sessionId = sessionId,
                actorId = "",
                suit = Suit.ZALUDY,
                gameType = GameType.TRI,
                flekLevel = 0,
                won = false,
                timestamp = cz.lhoracek.mapl.data.repository.currentTimeMillis(),
                bet = sessionData.session.currentBet,
                noCaller = true,
            )
            repository.addRound(sessionId, round)
            _uiState.update { it.copy(isSaving = false, savedOk = true) }
        }
    }

    fun consumeNavigation() = _uiState.update { it.copy(savedOk = false) }
}
